import React, { FC } from 'react';

import { Box } from '@mui/system';
import { Typography } from '@mui/material';

import {
  IAdminBookedPharmacistDetails,
  IAdminRequestData,
  IAdminWorkshiftDetails,
  ICalendarEventType,
  IPharmacistEvent,
  PharmacistLegendVariant,
  WorkshiftStatus,
  convertToDecimals,
  counterOffer,
  currency,
  getHourlyLabel,
  getProfessionalLongTermBookings,
} from '@pharmaplan/common';

import RequestContractModal from '../../ServiceWorkshiftDetails/RequestContractModal';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { setDialog } from '../../../../reducers/dialogReducer';
import {
  ChipType,
  Constants,
  toWeekdayWeekendFormat,
} from '../../../../helpers/Constants';
import WithSkeleton from '../../../common/WithSkeleton';
import CustomButton from '../../../common/CustomButton';
import useAdmin from '../../../../hooks/useAdmin';
import strings from '../../../../localization';

import classes from './styles';
import ViewLongTermBookings from '../../ViewLongTermBookings';
import CustomChip from '../../../common/CustomChip';

const { requested, booked, workshift } = PharmacistLegendVariant;
interface IPharmacyCardFooter {
  type?: ICalendarEventType;
  event:
    | (Partial<IPharmacistEvent> &
        Partial<IAdminWorkshiftDetails | IAdminRequestData>)
    | Partial<IAdminBookedPharmacistDetails>;
  successAction: string;
  isPromoBooking?: boolean;
}

const PharmacyCardFooter: FC<IPharmacyCardFooter> = ({
  type,
  event,
  successAction,
  isPromoBooking,
}) => {
  const dispatch = useAppDispatch();
  const counterOfferEvent = useAppSelector(counterOffer);
  const isRequestedOrBooked = type === PharmacistLegendVariant.requested
    || type === PharmacistLegendVariant.booked;

  const {
    hourlyRate,
    softwareName,
    counterOfferId,
    longTermShiftContract,
    shifts = [],
    counterShiftContract,
  } = (event as IPharmacistEvent) || {};
  const { counterHourlyRate } = counterOfferEvent || {};

  const isValidHourlyRate = counterOfferId && counterHourlyRate !== hourlyRate;

  const requestedShifts = shifts?.reduce((acc, curr) => {
    if (curr.status === WorkshiftStatus.Requested) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const totalShifts = shifts?.length;

  const {
    weekDayPaidHourlyRate: counterWeekdayRate,
    weekendPaidHourlyRate: counterWeekendRate,
  } = counterShiftContract ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    longTermShiftContractId,
  } = longTermShiftContract ?? {};

  const isLongTerm = !!longTermShiftContractId;
  const weekRate = counterWeekdayRate || weekDayPaidHourlyRate;
  const weekendRate = counterWeekendRate || weekendPaidHourlyRate;

  const { isAdmin } = useAdmin();
  let hourlyRateLabel = strings.hourlyRate;
  let hourlyRates = `${currency}${convertToDecimals(hourlyRate)} /Hr`;

  if (isAdmin) {
    hourlyRateLabel = strings.pharmacyHourlyRate;
  }

  if (
    (counterWeekdayRate && counterWeekdayRate !== weekDayPaidHourlyRate)
    || (counterWeekendRate && counterWeekendRate !== weekendPaidHourlyRate)
  ) {
    hourlyRateLabel = strings.counterOfferRate;
  }

  if (isLongTerm && !isPromoBooking) {
    hourlyRateLabel += ` ${getHourlyLabel(weekRate, weekendRate)}`;
    hourlyRates = toWeekdayWeekendFormat(weekRate, weekendRate);
  }

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: 'text',
  })(Typography);

  const SkeletonBox = WithSkeleton({
    successAction, variant: 'rectangular',
  })(Box);

  const openContractRequestModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.viewOfferAndRequestBooking,
        },
        Component: <RequestContractModal />,
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const getEvent = () => {
    dispatch(getProfessionalLongTermBookings(longTermShiftContractId ?? ''));
  };

  const openBookingModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.viewOfferAndRequestBooking,
        },
        Component: <ViewLongTermBookings getEvent={getEvent} />,
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const labelConfig = {
    [workshift]: {
      label: '',
      onClick: null,
    },
    [requested]: {
      label: `${strings.viewRequestedShifts} (${requestedShifts ?? 0}/${
        totalShifts
      })`,
      onClick: openContractRequestModal,
    },
    [booked]: {
      label: strings.viewAllBookings,
      onClick: openBookingModal,
    },
  };

  return (
    <>
      <Box sx={classes.mainContainer}>
        <Box sx={classes.itemContainer}>
          <SkeletonTypography sx={classes.title}>
            {hourlyRateLabel}
          </SkeletonTypography>
          <Box sx={classes.valueContainer}>
            {isValidHourlyRate ? (
              <>
                <SkeletonTypography sx={classes.strike}>
                  {hourlyRates}
                </SkeletonTypography>
                <SkeletonTypography sx={classes.value}>
                  $
                  {convertToDecimals(counterHourlyRate)}
                </SkeletonTypography>
              </>
            ) : (
              <SkeletonBox>
                <CustomChip
                  type={
                  isPromoBooking ? ChipType.promoBooking : ChipType.hourlyRate
                }
                  label={hourlyRates}
                />
              </SkeletonBox>
            )}
          </Box>
        </Box>
        <Box sx={classes.itemContainer}>
          <SkeletonTypography sx={classes.title}>
            {strings.software}
          </SkeletonTypography>
          <SkeletonTypography sx={classes.value}>
            {softwareName}
          </SkeletonTypography>
        </Box>
      </Box>

      {isLongTerm && isRequestedOrBooked && (
        <CustomButton
          variant={Constants.variant.text}
          onClick={labelConfig?.[type]?.onClick}
          customButtonStyle={classes.counterOfferBtn}
          customClass={classes.counterOfferContainer}
          label={labelConfig?.[type]?.label}
        />
      )}
    </>
  );
};

export default PharmacyCardFooter;
